import { type TPageType } from "@/types/types";
type TSmartPageGroupTagName = TPageType | "RESULT";

/**
 * 根据当前页面类型和页面关系数据, 动态注册smartPageHistory方法
 * @param param
 * @returns
 */
export const registerSmartPageHistory = (
  { pageType, relatedPageInfoList, onePageInfo },
  navigate,
) => {
  // 获取目标页面路径的方法
  const getTargetPath = ({
    pageTagName,
    datasetId,
    dataid,
  }: {
    pageTagName: TSmartPageGroupTagName;
    datasetId?: string;
    dataid?: string;
  }) => {
    // step 1. 从 relatedPageInfoList 中匹配 datasetId 对应的 pageInfo
    let targetPageInfo;
    if (datasetId) {
      // 有 datasetId 时, 从 relatedPageInfoList 中匹配 datasetId 对应的 pageInfo
      targetPageInfo = relatedPageInfoList.find(
        (item) => item.datasetId === Number(datasetId),
      )?.pageInfo;
    } else {
      // 保持兜底逻辑, 旧的页面没有 datasetId, 默认取第一个
      targetPageInfo = relatedPageInfoList[0]?.pageInfo;
    }

    // step 2. 从 pageInfo 中匹配关键类型
    const ftpPageInfo = targetPageInfo.find((item) => item.type === "FTP");
    const formPageInfo = targetPageInfo.find((item) => item.type === "CREATE");

    // step 3. 根据 pageInfo 信息, 定义页面跳转映射配置
    const pageNavigationConfig = {
      CREATE: {
        CREATE: null, // 当前就是form, 什么都不做
        FTP: `/${ftpPageInfo?.code}`,
        RESULT: `./result`,
        UPDATE: `./update`,
        DETAIL: `./detail`,
      },
      UPDATE: {
        CREATE: "../",
        FTP: `/${ftpPageInfo?.code}`,
        RESULT: `../result`,
        UPDATE: null, // 当前就是update, 什么都不做
        DETAIL: `../detail`,
      },
      FTP: {
        CREATE: `/${formPageInfo?.code}`,
        FTP: null, // 当前就是ftp, 什么都不做
        RESULT: null, // 没用到
        UPDATE: `/${formPageInfo?.code}/update`,
        DETAIL: `/${formPageInfo?.code}/detail`,
      },
    };

    // step 4. 覆盖 onePage 模式下的跳转映射配置
    if (onePageInfo) {
      pageNavigationConfig.CREATE = {
        CREATE: null, // 当前就是form, 什么都不做
        FTP: `/${ftpPageInfo?.code}`,
        RESULT: `../`, // 本来是去result页的, onePage模式下直接返回上一层
        UPDATE: null, // 没用到
        DETAIL: null, // 没用到
      };
      pageNavigationConfig.UPDATE = {
        CREATE: null, // 没用到
        FTP: `/${ftpPageInfo?.code}`,
        RESULT: `../`, // 本来是去result页的, onePage模式下直接返回上一层
        UPDATE: null, // 当前就是update, 什么都不做
        DETAIL: null, // 没用到
      };
    }

    // 拼接返回完整的 path
    return `${pageNavigationConfig[pageType]?.[pageTagName]}${dataid ? `?dataid=${dataid}` : ""}`;
  };

  // 创建导航方法
  const createNavigationMethod = (isReplace = false) => {
    return ({
      pageTagName,
      datasetId,
      dataid,
    }: {
      pageTagName: TSmartPageGroupTagName;
      datasetId?: string;
      dataid?: string;
    }) => {
      const targetPath = getTargetPath({ pageTagName, datasetId, dataid });
      if (targetPath !== null && targetPath !== undefined) {
        if (isReplace) {
          // 使用 replace 模式导航
          navigate(targetPath, { replace: true });
        } else {
          // 使用 push 模式导航
          navigate(targetPath);
        }
      }
    };
  };

  return {
    push: createNavigationMethod(false),
    replace: createNavigationMethod(true),
  };
};
